import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Clapperboard, FileText, Link2, Video } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { localeUI } from '../lib/format';
import { AtrVideoPlayer } from './AtrVideoPlayer';
import { fmtBytes, fmtDuration, type AtrVideoDTO } from '../lib/atrVideos';

/**
 * "VIDEOS" DEL CLAIM (pedido del dueño 2026-09-19: "¿dónde puedo ver los videos que subí en cada
 * cliente? Lo quiero en Claims, en Scheduling y en Overview; sepáralos, marca los ATR y los
 * informativos, y un botón para ver el reporte de la inspección"). Todo lo que el inspector subió
 * desde el app de campo para este claim, en dos grupos (ATR / informativos), cada uno con la visita
 * en la que se grabó, quién lo subió, el reproductor plegado y el reporte de esa visita a un click.
 * El cuadro no aparece si el claim no tiene videos.
 *
 * Distinto del cuadro ATR (que es el flujo de Review ATR: aceptar y generar el link): esto es para VER.
 */
type Cita = { id: string; scheduledStart: string | null; type: string | null; inspectorName: string | null; checkedOutAt: string | null; resultNotes: string | null; atrRoofCondition: string | null; atrOpinion: string | null };
export type VideoDelClaim = AtrVideoDTO & { appointment: Cita | null };

const TIPO: Record<string, string> = { inspection: 'Inspection', adjuster_meeting: 'Reinspection', atr: 'ATR', atr_on_site: 'ATR on site', photos: 'Photos', check_pickup: 'Check pickup', material_sample: 'Material sample', repair_video: 'Repair video', custom: 'Visit' };
const fecha = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const hora = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString(localeUI(), { hour: 'numeric', minute: '2-digit' }) : '');

export function useVideosDelClaim(claimId: string): VideoDelClaim[] | null {
  const [videos, setVideos] = useState<VideoDelClaim[] | null>(null);
  useEffect(() => {
    let vivo = true;
    setVideos(null);
    api.get<{ videos: VideoDelClaim[] }>(`/api/claims/${claimId}/videos`).then((r) => { if (vivo) setVideos(r.videos); }).catch(() => { if (vivo) setVideos([]); });
    return () => { vivo = false; };
  }, [claimId]);
  return videos;
}

/** El reporte de la visita en la que se grabó el video (lo que escribió el inspector al hacer check-out). */
function Reporte({ a }: { a: Cita }) {
  const { t } = useI18n();
  const hay = a.resultNotes || a.atrRoofCondition || a.atrOpinion;
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px', display: 'grid', gap: 4, fontSize: 'var(--fs-xs)' }}>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        <FileText size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cvid.reportTitle')}
        {a.inspectorName ? ` · ${a.inspectorName}` : ''}{a.checkedOutAt ? ` · ${t('cvid.checkedOut', { time: `${fecha(a.checkedOutAt)} ${hora(a.checkedOutAt)}` })}` : ''}
      </div>
      {hay ? (
        <>
          {a.resultNotes && <div style={{ whiteSpace: 'pre-wrap' }}>{a.resultNotes}</div>}
          {a.atrRoofCondition && <div><b>{t('cd.roofCondition')}:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{a.atrRoofCondition}</span></div>}
          {a.atrOpinion && <div><b>{t('cd.atrOpinion')}:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{a.atrOpinion}</span></div>}
        </>
      ) : <div className="muted">{t('cvid.noReport')}</div>}
    </div>
  );
}

function Fila({ v }: { v: VideoDelClaim }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [reporte, setReporte] = useState(false);
  const info = [fmtBytes(v.sizeBytes), fmtDuration(v.durationMs), v.width && v.height ? `${v.width}×${v.height}` : ''].filter(Boolean).join(' · ');
  return (
    <details open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)} style={{ border: '1px solid var(--line)', borderRadius: 10 }}>
      <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Video size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.title || v.fileName}</span>
        <span className="muted" style={{ fontWeight: 400 }}>· {fecha(v.readyAt ?? v.createdAt)}</span>
        {v.appointment && <span className="muted" style={{ fontWeight: 400 }}>· {TIPO[v.appointment.type ?? ''] ?? t('cvid.visit')} {fecha(v.appointment.scheduledStart)}</span>}
        <span style={{ flex: 1 }} />
        {v.share && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)' }}><Link2 size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.linked')}</span>}
        {v.codec === 'hevc' && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--orange-soft)', color: 'var(--orange)' }} title={t('atr.hevcWarn')}>HEVC</span>}
        {v.appointment && (
          <button type="button" className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReporte((r) => !r); setOpen(true); }}>
            <FileText size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cvid.report')}
          </button>
        )}
      </summary>
      <div style={{ padding: '0 10px 10px', display: 'grid', gap: 6 }}>
        {reporte && v.appointment && <Reporte a={v.appointment} />}
        <AtrVideoPlayer url={v.mediaUrl} codec={v.codec} />
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
          {v.fileName}{info ? ` · ${info}` : ''}{v.uploadedBy ? ` · ${t('rec.by')} ${v.uploadedBy}` : ''}
          {v.share ? <> · <a href={v.share.url} target="_blank" rel="noopener noreferrer">{t('cvid.publicLink')} ↗</a></> : null}
        </div>
      </div>
    </details>
  );
}

function Seccion({ titulo, videos }: { titulo: string; videos: VideoDelClaim[] }) {
  if (videos.length === 0) return null;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{titulo} <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{videos.length}</span></div>
      {videos.map((v) => <Fila key={v.id} v={v} />)}
    </div>
  );
}

export function ClaimVideosBox({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const videos = useVideosDelClaim(claimId);
  const loc = useLocation();
  // Deep-link desde el chip de la cabecera (#sec-videos): el cuadro se trae a la vista cuando ya cargó.
  useEffect(() => {
    if (!videos?.length || (loc.hash || '').replace(/^#/, '') !== 'sec-videos') return;
    document.getElementById('sec-videos')?.scrollIntoView({ block: 'center' });
  }, [videos, loc.hash]);
  if (!videos || videos.length === 0) return null;
  const atr = videos.filter((v) => v.kind === 'atr');
  const info = videos.filter((v) => v.kind !== 'atr');
  return (
    <div id="sec-videos" className="card">
      <h2 className="card-title">
        <Clapperboard size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cvid.title')} <span className="badge gray">{videos.length}</span>
      </h2>
      <div style={{ display: 'grid', gap: 12 }}>
        <Seccion titulo={t('cvid.atrSection')} videos={atr} />
        <Seccion titulo={t('cvid.infoSection')} videos={info} />
      </div>
    </div>
  );
}

/**
 * EL CHIP DE LA CABECERA ("ATR video available" / "Informative video available"): al lado de la
 * dirección, donde sobra espacio a la derecha. Un click lleva al cuadro Videos. No se pinta sin videos.
 */
export function ClaimVideosChip({ claimId, onGo }: { claimId: string; onGo: () => void }) {
  const { t } = useI18n();
  const videos = useVideosDelClaim(claimId);
  if (!videos || videos.length === 0) return null;
  const atr = videos.filter((v) => v.kind === 'atr').length;
  const info = videos.length - atr;
  const chip = (texto: string, n: number, tono: 'green' | 'accent') => (
    <button type="button" className="badge" onClick={onGo} title={t('cvid.chipGo')}
      style={{ cursor: 'pointer', border: 0, fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: tono === 'green' ? 'var(--green-soft)' : 'var(--accent-soft)', color: tono === 'green' ? 'var(--green)' : 'var(--accent)' }}>
      <Video size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {texto}{n > 1 ? ` (${n})` : ''}
    </button>
  );
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {atr > 0 && chip(t('cvid.chipAtr'), atr, 'green')}
      {info > 0 && chip(t('cvid.chipInfo'), info, 'accent')}
    </span>
  );
}
